import { createContext, useContext, useMemo, useReducer } from "react"
import { usePreferences } from "hooks/use-preferences"

export const FactorsViewContext = createContext()

const PREFERENCE_SUBTYPE = "factors-view"

const DEFAULT_STATE = {
  view: "performance",
  groupBy: null,
  showMetricsSelector: false,
  selectedPeriodPerformance: "1M",
  // ContributorsTable sorting state
  sortMetricId: null,
  sortDirection: "DESC",
  // ContributorsTable navigation state
  selectedTableLevels: [],
  // DataGrid state persistence
  datagridState: {},
}

// Define which fields should be saved as preferences
const PREFERENCE_FIELDS = [
  "view",
  "groupBy",
  "showNotHeld",
  "selectedPeriodPerformance",
  "sortMetricId",
  "sortDirection",
  "selectedTableLevels",
  "datagridState",
]

function reducer(current, action) {
  switch (action.type) {
    case "setView":
      return {
        ...current,
        view: action.view,
      }
    case "setGroupBy":
      return {
        ...current,
        groupBy: action.groupBy,
      }
    case "setShowNotHeld":
      return {
        ...current,
        showNotHeld: action.showNotHeld,
      }
    case "setShowMetricsSelector":
      return {
        ...current,
        showMetricsSelector: action.showMetricsSelector,
      }
    case "setSelectedPeriodPerformance":
      return {
        ...current,
        selectedPeriodPerformance: action.selectedPeriodPerformance,
      }
    case "setSortMetricId":
      return {
        ...current,
        sortMetricId: action.sortMetricId,
      }
    case "setSortDirection":
      return {
        ...current,
        sortDirection: action.sortDirection,
      }
    case "setSelectedTableLevels":
      return {
        ...current,
        selectedTableLevels: action.selectedTableLevels,
      }
    case "setDatagridState":
      return {
        ...current,
        datagridState: action.datagridState,
      }
    case "setPreferences":
      return {
        ...current,
        ...(action.preferences || {}),
      }
    default:
      return current
  }
}

function setView(view) {
  return {
    type: "setView",
    view,
  }
}

function setGroupBy(groupBy) {
  return {
    type: "setGroupBy",
    groupBy,
  }
}

function setShowNotHeld(showNotHeld) {
  return {
    type: "setShowNotHeld",
    showNotHeld,
  }
}

function setShowMetricsSelector(showMetricsSelector) {
  return {
    type: "setShowMetricsSelector",
    showMetricsSelector,
  }
}

function setSortMetricId(sortMetricId) {
  return {
    type: "setSortMetricId",
    sortMetricId,
  }
}

function setSortDirection(sortDirection) {
  return {
    type: "setSortDirection",
    sortDirection,
  }
}

function setSelectedTableLevels(selectedTableLevels) {
  return {
    type: "setSelectedTableLevels",
    selectedTableLevels,
  }
}

function setDatagridState(datagridState) {
  return {
    type: "setDatagridState",
    datagridState,
  }
}

function setPreferences(preferences) {
  return {
    type: "setPreferences",
    preferences,
  }
}

export default function useFactorsViewContext(parentContext) {
  const parentContextValue = useContext(parentContext)
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setView: (...args) => dispatch(setView(...args)),
    setGroupBy: (...args) => dispatch(setGroupBy(...args)),
    setShowNotHeld: (...args) => dispatch(setShowNotHeld(...args)),
    setShowMetricsSelector: (...args) => dispatch(setShowMetricsSelector(...args)),
    setSortMetricId: (...args) => dispatch(setSortMetricId(...args)),
    setSortDirection: (...args) => dispatch(setSortDirection(...args)),
    setSelectedTableLevels: (...args) => dispatch(setSelectedTableLevels(...args)),
    setDatagridState: (...args) => dispatch(setDatagridState(...args)),
  }), [dispatch])

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...contextUpdaters,
    ...state,
    isPreferencesLoaded,
  }), [state, parentContextValue, contextUpdaters, isPreferencesLoaded])

  return [FactorsViewContext, contextValue]
}
